import { defineStore } from 'pinia'
import { readonly, ref } from 'vue'

import api from '@/client/core/api'
import { useSystemStore } from '@/client/store/useSystemStore'
import { TCampaign } from '@/typings/api/campaigns.type'
import { TPutCandidateParams, TPutWinnerParams } from '@/typings/api/instagram_applicants.type'
import { TPrize } from '@/typings/api/instagram_campaign_prizes.type'
import { TGetInstagramPostsParams, TInstagramPost, TPutInstagramPostsParams } from '@/typings/api/instagram_posts.type'

type UpdatePostsResponse = {
  data?: unknown
  error?: { type?: string }
}

type PostsState = {
  posts: TInstagramPost[]
  total: number
  entryCount: number
  campaign: TCampaign | null
  params: TGetInstagramPostsParams
  prizes: TPrize[]
  error: boolean
  loading: boolean
  firstLoaded: boolean
}

const createInitialState = (): PostsState => ({
  posts: [],
  total: 0,
  entryCount: 0,
  campaign: null,
  params: {
    campaignId: 0,
    sortBy: '-publishDatetime',
    pagingNo: 1,
    limitCount: 25,
    prizeId: '',
    isFollowing: '',
    isCandidate: '',
    isWidget: '',
    isWinner: '',
    accountName: '',
    entryDateFrom: '',
    entryDateTo: ''
  },
  prizes: [],
  error: false,
  loading: false,
  firstLoaded: false
})

export const useInstagramPostsStore = defineStore('instagramPostsStore', () => {
  const systemStore = useSystemStore()
  const initialState = createInitialState()

  const posts = ref<PostsState['posts']>(initialState.posts)
  const total = ref<PostsState['total']>(initialState.total)
  const entryCount = ref<PostsState['entryCount']>(initialState.entryCount)
  const campaign = ref<PostsState['campaign']>(initialState.campaign)
  const params = ref<PostsState['params']>(initialState.params)
  const prizes = ref<PostsState['prizes']>(initialState.prizes)
  const error = ref<PostsState['error']>(initialState.error)
  const loading = ref<PostsState['loading']>(initialState.loading)
  const firstLoaded = ref<PostsState['firstLoaded']>(initialState.firstLoaded)

  const reset = async (): Promise<void> => {
    const nextState = createInitialState()
    posts.value = nextState.posts
    total.value = nextState.total
    entryCount.value = nextState.entryCount
    campaign.value = nextState.campaign
    params.value = nextState.params
    prizes.value = nextState.prizes
    error.value = nextState.error
    loading.value = nextState.loading
    firstLoaded.value = nextState.firstLoaded
  }

  const fetchPosts = async (): Promise<void> => {
    error.value = false
    loading.value = true
    await systemStore.setLoading(true)

    const result = await api.get('/instagram_posts', { params: params.value })

    await systemStore.setLoading(false)
    loading.value = false

    if (!result.data) {
      error.value = true
      return
    }

    if (!firstLoaded.value) {
      firstLoaded.value = true
      entryCount.value = result.data.dataCount
    }

    posts.value = result.data.posts
    if (result.data.dataCount !== undefined) {
      total.value = result.data.dataCount
    }
  }

  const fetchCampaign = async (): Promise<void> => {
    error.value = false

    const result = await api.get(`/campaigns/${params.value.campaignId}`)

    if (!result.data) {
      error.value = true
      return
    }

    campaign.value = result.data
  }

  const fetchPrizes = async (): Promise<void> => {
    error.value = false

    const result = await api.get('/instagram_campaign_prizes', {
      params: {
        campaignId: params.value.campaignId,
        limitCount: -1
      }
    })

    if (!result.data) {
      error.value = true
      return
    }

    prizes.value = result.data.prizes
  }

  const setParams = (newParams: Partial<TGetInstagramPostsParams>): void => {
    params.value = {
      ...params.value,
      ...newParams,
      pagingNo: newParams.pagingNo || 1
    }
  }

  const updateIsWidget = async (payload: TPutInstagramPostsParams): Promise<UpdatePostsResponse> => {
    const result: UpdatePostsResponse = await api.put('/instagram_posts/' + payload.postId, {
      campaignId: params.value.campaignId,
      isWidget: payload.isWidget ? 0 : 1
    })

    if (result.data) {
      await fetchPosts()
    }

    return result
  }

  const updateIsCandidate = async (payload: TPutCandidateParams): Promise<UpdatePostsResponse> => {
    const result: UpdatePostsResponse = await api.put('/instagram_applicants/' + payload.applicantId, {
      isCandidate: payload.isCandidate ? 0 : 1
    })

    if (result.data) {
      await fetchPosts()
    }

    return result
  }

  const updateIsWinner = async (payload: TPutWinnerParams): Promise<UpdatePostsResponse> => {
    const result: UpdatePostsResponse = await api.put('/instagram_applicants/' + payload.applicantId, {
      prizeId: payload.prizeId,
      isWinner: payload.isWinner ? 0 : 1,
      wasWinner: payload.wasWinner
    })

    if (result.data) {
      await fetchPosts()
    }

    return result
  }

  const updateNgAccount = async (payload: { screenName: string; isNg: boolean; ngApplicantId: number | null }) => {
    if (!campaign.value) return null
    if (!payload.isNg && !payload.ngApplicantId) return null

    const response = payload.isNg
      ? await api.post('/instagram_ng_applicants', {
          groupId: campaign.value.groupId,
          screenNames: [payload.screenName]
        })
      : await api.delete(`/instagram_ng_applicants/${payload.ngApplicantId}`)

    if (response.data) await fetchPosts()
    return response
  }

  return {
    posts: readonly(posts),
    total: readonly(total),
    entryCount: readonly(entryCount),
    campaign: readonly(campaign),
    params: readonly(params),
    prizes: readonly(prizes),
    error: readonly(error),
    loading: readonly(loading),
    firstLoaded: readonly(firstLoaded),
    reset,
    fetchPosts,
    fetchCampaign,
    fetchPrizes,
    setParams,
    updateIsWidget,
    updateIsCandidate,
    updateIsWinner,
    updateNgAccount
  }
})
